/* eslint-disable @typescript-eslint/no-explicit-any */
import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import LoadingScreen from 'components/LoadingScreen';
import ModalDialog from 'components/ModalDialog';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import { Row, Col, Card, CardBody, Button } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { findingGridDataRefreshAtom, selectedRowIdsAtom } from './state';

type Props = {
  onClose: () => void;
};

const WatcherBulkModal = ({ onClose }: Props) => {
  const selectedFindingIds = useAtomValue(selectedRowIdsAtom);
  const refreshFindingsGridData = useSetAtom(findingGridDataRefreshAtom);

  const company_id = useAtomValue(companyIdAtom);
  const [loading, setLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState('');
  const [members, setMembers] = useState([]);
  const [current_watcher, setCurrentWatcher] = useState<any>();

  // useEffect
  useEffect(() => {
    const fetchMembers = async () => {
      // fetch members
      setLoading(true);
      let url = '/profiles/colleague/?search=&is_active=True';
      url += '&company=' + company_id;

      await callApi(url, 'GET', {})
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            if (res.data.results) {
              setMembers(res.data.results);
            } else {
              setMembers([]);
            }
          } else if (res.status === 422) {
          }
        })
        .catch((err) => {
          setError('Error fetching members' + err);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchMembers();
  }, [company_id]);

  const handleSave = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    action: string,
  ): Promise<void> => {
    event.preventDefault();
    setIsAssigning(true);
    setError('');

    try {
      const data = {
        finding_ids: selectedFindingIds.join(','),
        watcher_user_ids: current_watcher
          .map((item) => item.users_id)
          .join(','),
        action: action,
      };
      const response = await callApi(
        '/assignee_maps/multi-vunl-watcher-map/',
        'POST',
        {},
        data,
      );

      if (response.status === 200 || response.status === 201) {
        onClose();
        if (action == 'assign') {
          showSuccessToast('Watcher successfully assigned');
        } else {
          showSuccessToast('Watcher successfully unassigned');
        }
        refreshFindingsGridData(Math.random().toString());
      } else {
        if (action == 'assign') {
          showFailureToast('Failed to assign watcher');
        } else {
          showFailureToast('Failed to unassign watcher');
        }
        setError('Failed to assign/unassign watcher');
      }
    } catch (err) {
      showFailureToast('Failed to update findings watcher');
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <ModalDialog
      size="md"
      title="Add/Remove Findings Watcher"
      subTitle={
        <>
          <strong>{selectedFindingIds?.length}</strong> findings selected
        </>
      }
      childComponent={
        loading ? (
          <LoadingScreen />
        ) : (
          <CustomModalWrapper id="finding-assignee-scroll">
            <Row>
              <Col>
                <Card>
                  <CardBody className="mt-3">
                    <p>
                      Select the watcher for the selected findings. Click
                      assign/unassign button to add/remove the watchers.
                      <br />
                      <br />
                      Note: If the selected findings already have the assigned
                      watcher, that specific watcher will be skipped, and new
                      watchers will be added only. The same logic applies when
                      unassigning watchers.
                    </p>
                    <Row className="form-group">
                      <Col xs="auto" className="d-flex align-items-center">
                        <label>
                          <strong>Watchers</strong>
                        </label>
                      </Col>
                      <Col>
                        <Select
                          className="custom-360-dropdown profile-pic-dropdown"
                          closeMenuOnSelect={false}
                          isMulti
                          isClearable={true}
                          hideSelectedOptions={true}
                          onChange={(e) => {
                            setCurrentWatcher(e as any);
                          }}
                          value={current_watcher}
                          options={members}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>{error && <div>{error}</div>}</Col>
            </Row>
          </CustomModalWrapper>
        )
      }
      customFooter={
        <Row className="w-100">
          <Col className="text-left" xs>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
          </Col>
          <Col className="text-right" xs="auto">
            <Button
              color="secondary"
              onClick={(event) => handleSave(event, 'unassign')}
              disabled={!current_watcher}
              className="mr-2"
            >
              Remove Watcher
            </Button>
            &nbsp;&nbsp;
            {isAssigning ? (
              <CircleSpinner size={20} color="#0665a9" loading={true} />
            ) : (
              <Button
                color="primary"
                onClick={(event) => handleSave(event, 'assign')}
                disabled={!current_watcher}
              >
                Add Watcher
              </Button>
            )}
          </Col>
        </Row>
      }
      onClose={() => onClose()}
    />
  );
};

export default WatcherBulkModal;
